import { getConfig } from '../core/config';
import type { SelectedElement } from '../core/state';

export interface DomSnapshot {
  selectedHtml: string;
  parentContext: string;
  totalDomSize: number;
}

const MAX_SELECTED_HTML_ENTRY_CHARS = 20_000;
const MAX_PARENT_CONTEXT_ENTRY_CHARS = 30_000;
const MAX_SELECTED_HTML_TOTAL_CHARS = 80_000;
const MAX_PARENT_CONTEXT_TOTAL_CHARS = 120_000;
const MAX_SERIALIZE_NODES = 1500;
const TRUNCATED_SUFFIX = '...[truncated]';

function maskSensitive(html: string): string {
  // Mask password input values
  html = html.replace(
    /(<input[^>]*type\s*=\s*["']password["'][^>]*value\s*=\s*["'])[^"']*?(["'][^>]*>)/gi,
    '$1****$2'
  );
  // Mask data-sensitive elements content
  html = html.replace(
    /(<[^>]*data-sensitive[^>]*>)[\s\S]*?(<\/[^>]+>)/gi,
    '$1[REDACTED]$2'
  );
  return html;
}

function truncateString(input: string, maxChars: number): string {
  if (input.length <= maxChars) return input;
  return input.slice(0, maxChars) + TRUNCATED_SUFFIX;
}

function isSubtreeTooLarge(element: HTMLElement, nodeLimit: number): boolean {
  let count = 0;
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT);
  while (walker.nextNode()) {
    count++;
    if (count > nodeLimit) return true;
  }
  return false;
}

function getSafeOuterHtml(element: HTMLElement, maxChars: number): string {
  if (isSubtreeTooLarge(element, MAX_SERIALIZE_NODES)) {
    return `<${element.tagName.toLowerCase()} data-br-truncated="subtree-too-large">${TRUNCATED_SUFFIX}</${element.tagName.toLowerCase()}>`;
  }
  return truncateString(element.outerHTML, maxChars);
}

function getParentContext(element: HTMLElement, levels: number): string {
  let current: HTMLElement | null = element;
  for (let i = 0; i < levels; i++) {
    if (current.parentElement && current.parentElement !== document.documentElement) {
      current = current.parentElement;
    } else {
      break;
    }
  }
  if (!current) return '';

  return getSafeOuterHtml(current, MAX_PARENT_CONTEXT_ENTRY_CHARS);
}

function appendWithTotalLimit(parts: string[], chunk: string, maxChars: number): void {
  const currentTotal = parts.reduce((sum, part) => sum + part.length, 0);
  if (currentTotal >= maxChars) return;
  const remaining = maxChars - currentTotal;
  parts.push(truncateString(chunk, remaining));
}

export function getDomSnapshot(selectedElements: SelectedElement[]): DomSnapshot {
  const config = getConfig();

  const selectedHtmlParts: string[] = [];
  const parentContextParts: string[] = [];

  for (const sel of selectedElements) {
    let html = getSafeOuterHtml(sel.element, MAX_SELECTED_HTML_ENTRY_CHARS);
    if (config.maskInputs) {
      html = maskSensitive(html);
    }
    html = truncateString(html, MAX_SELECTED_HTML_ENTRY_CHARS);
    appendWithTotalLimit(selectedHtmlParts, html, MAX_SELECTED_HTML_TOTAL_CHARS);

    let parentHtml = getParentContext(sel.element, 3);
    if (config.maskInputs) {
      parentHtml = maskSensitive(parentHtml);
    }
    parentHtml = truncateString(parentHtml, MAX_PARENT_CONTEXT_ENTRY_CHARS);
    appendWithTotalLimit(parentContextParts, parentHtml, MAX_PARENT_CONTEXT_TOTAL_CHARS);
  }

  return {
    selectedHtml: selectedHtmlParts.join('\n---\n'),
    parentContext: parentContextParts.join('\n---\n'),
    totalDomSize: Math.min(document.getElementsByTagName('*').length, 100000),
  };
}
